


































































































































































































import {
  Component,
  Mixins,
  Prop,
  Watch,
} from 'vue-property-decorator';
import { Validations } from 'vuelidate-property-decorators';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import PretensionController from '@/controllers/Pretension';
import {
  StartSigningConflictData,
  StartSigningPreviousAttempt,
  StartSigningRequest,
} from '@/controllers/PretensionSigning.types';
import { TaskResponse } from '@/classes/task/types';
import { PretensionStatus } from '@/types/Pretension';
import { statusReason } from '@/utils/pretensionStatusHistoryDisplay';

// eslint-disable-next-line no-shadow
enum SigningModalState {
  FORM = 'form',
  LOADING = 'loading',
  SUCCESS = 'success',
  CONFLICT_LOCKED = 'conflict_locked',
  CONFLICT_STATUS = 'conflict_status',
  CONFLICT_SIGNER_BUSY = 'conflict_signer_busy',
  ERROR = 'error',
  DIADOC_REFUSED = 'diadoc_refused',
}

// 14135: StartSigning отвечает 2xx сразу (сигнал в jBPM), а ответ Диадока приходит через
// 0,3–0,6 с статусом претензии. Окно опрашивает карточку, пока статус в этих значениях.
const VERDICT_PENDING_STATUSES: string[] = [
  PretensionStatus.SIGNER_SIGNING_AWAIT,
  PretensionStatus.SIGNER_SIGNING_IN_PROGRESS,
];

// Sync with backend setting PRETENSION_SIGNING_LOCK_TTL_MIN —
// pretensions-signing-php-spec.md §A2. Если в payload пришёл lockTtlMinutes —
// используем его (I-03); иначе fallback на эту константу.
const LOCK_TTL_MINUTES = 20;

@Component
export default class SigningModal extends Mixins(validationMixin) {
  @Prop({ required: true }) readonly value!: boolean;

  @Prop({ required: true }) readonly pretensionId!: number;

  @Prop({ required: true }) readonly signerEmail!: string;

  // Часть 2 (FML-khmj, §6): oidc-режим — без диалога логин/пароль.
  // Гейт сессии (ensureSignerSession) выполняет страница ДО открытия модалки;
  // модалка в oidc-режиме сразу инициирует подписание и показывает myDSS-ожидание.
  @Prop({ default: false }) readonly oidcMode!: boolean;

  private state: SigningModalState = SigningModalState.FORM;

  // expose enum for template (v-if comparisons)
  private SigningModalState = SigningModalState;

  private credentials = { login: '', password: '' };

  private showPassword = false;

  private conflictData: StartSigningConflictData | null = null;

  private errorMessage: string | null = null;

  // M-4 guard: предотвращает двойной emit('success') при OK→watcher
  private successEmitted = false;

  // 14135: причина отказа Диадока (последняя строка истории претензии).
  private refusedReason: string | null = null;

  // 14135: за время опроса ответ Диадока не пришёл — SUCCESS с честным текстом.
  private verdictPending = false;

  // 14135: номер текущего опроса; закрытие/повторное открытие окна его обрывает.
  private verdictRun = 0;

  // Тесты подменяют, чтобы не ждать.
  verdictPollMs = 1500;

  verdictTimeoutMs = 15000;

  @Validations()
  validations() {
    return {
      credentials: {
        login: { required },
        password: { required },
      },
    };
  }

  @Watch('value')
  onValueChanged(next: boolean, prev: boolean): void {
    if (!next && prev) {
      // Закрытие
      this.verdictRun += 1;
      if (this.state === SigningModalState.SUCCESS && !this.successEmitted) {
        this.successEmitted = true;
        this.$emit('success');
      }
      if (this.state === SigningModalState.LOADING) {
        // U-01 mandatory toast при закрытии из LOADING
        const bus = (this as any).$bus;
        if (bus && typeof bus.$emit === 'function') {
          bus.$emit(
            'notification',
            this.$t('pretension_signing_modal_loading_toast') as string,
            'info',
          );
        }
      }
    }
    if (next && !prev) {
      // Часть 2: в oidc-режиме формы нет — сразу инициируем подписание (myDSS-флоу).
      if (this.oidcMode) this.beginOidcSigning();
      else this.resetToForm();
    }
  }

  private resetToForm(): void {
    this.state = SigningModalState.FORM;
    this.credentials = { login: '', password: '' };
    this.showPassword = false;
    this.conflictData = null;
    this.errorMessage = null;
    this.successEmitted = false;
    this.refusedReason = null;
    this.verdictPending = false;
    this.verdictRun += 1;
    this.$v.$reset();
  }

  // Часть 2 (§6): oidc-старт без логин/пароля. Сессия уже провалидирована страницей
  // (ensureSignerSession). Payload без legacy-кредов — авторизация по OIDC-сессии.
  private beginOidcSigning(): void {
    this.conflictData = null;
    this.errorMessage = null;
    this.successEmitted = false;
    this.refusedReason = null;
    this.verdictPending = false;
    this.verdictRun += 1;
    this.state = SigningModalState.LOADING;
    this.runStartSigning({ id: this.pretensionId, signerEmail: this.signerEmail });
  }

  // Пароль НЕ тримим (пробел может быть валидной частью), но подсвечиваем
  // краевой пробел non-blocking-предупреждением — частый источник неверного
  // пароля Диадока из-за автозаполнения/копипаста. Отправку не блокируем.
  get hasPasswordEdgeSpace(): boolean {
    const p = this.credentials.password;
    return p.length > 0 && p.trim() !== p;
  }

  get passwordSpaceMessages(): string[] {
    return this.hasPasswordEdgeSpace
      ? [this.$t('pretension_signing_modal_password_space_warning') as string]
      : [];
  }

  get loadingTextKey(): string {
    if (this.awaitingVerdict) return 'pretension_signing_modal_waiting_text';
    return this.oidcMode
      ? 'pretension_signing_modal_oidc_loading_text'
      : 'pretension_signing_modal_loading_text';
  }

  get successTextKey(): string {
    if (this.verdictPending && !this.oidcMode) return 'pretension_signing_modal_pending_text';
    return this.oidcMode
      ? 'pretension_signing_modal_oidc_success_text'
      : 'pretension_signing_modal_success_text';
  }

  getValidationErrors(v: any): string[] {
    const errors: string[] = [];
    if (!v || v.$error === false || v.$invalid === false) return errors;
    if (v.required === false) errors.push('Обязательное поле');
    return errors;
  }

  async onSubmit(): Promise<void> {
    this.$v.$touch();
    if (this.$v.$invalid) return;
    // C-03: spinner сразу
    this.state = SigningModalState.LOADING;
    await this.runStartSigning({
      id: this.pretensionId, // CS-A
      signerEmail: this.signerEmail,
      diadocLogin: this.credentials.login,
      diadocPassword: this.credentials.password,
    });
  }

  // Общее ядро запроса start-signing для legacy (форма) и oidc (авто-старт).
  // Caller сам выставляет state=LOADING до вызова.
  private async runStartSigning(payload: StartSigningRequest): Promise<void> {
    try {
      const response = await PretensionController.startSigning(payload);
      this.handleResponse(response);
      if (this.state === SigningModalState.SUCCESS) await this.awaitDiadocVerdict();
    } catch (e) {
      this.errorMessage = String((e as any)?.message ?? e);
      this.state = SigningModalState.ERROR;
    }
  }

  // 14135: после 2xx окно ждёт, что ответил Диадок, а не сообщает об успехе сразу.
  // Отказ → DIADOC_REFUSED с причиной; подпись запущена → SUCCESS; ответа нет → SUCCESS
  // с текстом «ответ ещё не пришёл». Ошибка опроса не мешает: ждём следующий тик.
  private awaitingVerdict = false;

  private async awaitDiadocVerdict(): Promise<void> {
    this.verdictRun += 1;
    const run = this.verdictRun;
    const deadline = Date.now() + this.verdictTimeoutMs;
    this.state = SigningModalState.LOADING;
    this.awaitingVerdict = true;
    try {
      while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => { setTimeout(r, this.verdictPollMs); });
        if (run !== this.verdictRun) return;
        // eslint-disable-next-line no-await-in-loop
        const p = await this.fetchPretension();
        if (run !== this.verdictRun) return;
        if (p && !VERDICT_PENDING_STATUSES.includes(p.status)) {
          const refused = p.status === PretensionStatus.PROBLEM || p.status === PretensionStatus.RECALLED;
          if (refused) {
            this.refusedReason = statusReason(p);
            this.state = SigningModalState.DIADOC_REFUSED;
            this.$emit('stale-state');
          } else {
            this.state = SigningModalState.SUCCESS;
          }
          return;
        }
      }
      this.verdictPending = true;
      this.state = SigningModalState.SUCCESS;
    } finally {
      this.awaitingVerdict = false;
    }
  }

  private async fetchPretension(): Promise<any | null> {
    try {
      const response = await PretensionController.single(this.pretensionId);
      const [error, result] = response?.result ?? [];
      if (error) return null;
      return (Array.isArray(result?.result) ? result.result[0] : result?.result) ?? null;
    } catch (e) {
      return null;
    }
  }

  private handleResponse(response: TaskResponse): void {
    const [error, result] = response.result;
    if (response.code === 200 && !error) {
      const data = result?.data ?? result;
      if (data?.recovered && data.previousAttempt) {
        this.$emit('recovered', data.previousAttempt as StartSigningPreviousAttempt);
      }
      this.state = SigningModalState.SUCCESS;
      return;
    }
    if (response.code === 409) {
      const data = this.extractConflictData(error || result);
      this.conflictData = data;
      if (data?.reason === 'locked_by_other') {
        this.state = SigningModalState.CONFLICT_LOCKED;
      } else if (data?.reason === 'signer_busy_elsewhere') {
        // R-SS: у подписанта уже есть претензия в активном подписании — сериализация,
        // одновременно подписывается только одна. Текст с номером/временем — signerBusyText.
        this.state = SigningModalState.CONFLICT_SIGNER_BUSY;
      } else if (data?.reason === 'wrong_status') {
        this.state = SigningModalState.CONFLICT_STATUS;
        this.$emit('stale-state');
      } else if (data?.reason === 'not_found') {
        // X-02
        this.errorMessage = this.$t('pretension_signing_modal_not_found') as string;
        this.state = SigningModalState.ERROR;
      } else {
        this.errorMessage = this.$t('pretension_signing_modal_unknown_conflict') as string;
        this.state = SigningModalState.ERROR;
      }
      return;
    }
    if (response.code >= 500 && response.code < 600) {
      this.errorMessage = this.$t('pretension_signing_modal_kie_unavailable') as string;
      this.state = SigningModalState.ERROR;
      return;
    }
    // 401/403 — типовой сценарий истёкшего токена в sensitive flow подписания.
    // Без явного UX-сообщения пользователь видит «HTTP 401» и не знает, что делать.
    if (response.code === 401 || response.code === 403) {
      this.errorMessage = this.$t('pretension_signing_modal_auth_error') as string;
      this.state = SigningModalState.ERROR;
      return;
    }
    this.errorMessage = (typeof error === 'string' ? error : null) || `HTTP ${response.code}`;
    this.state = SigningModalState.ERROR;
  }

  // Контракт T1 §5 / R-SS-7: PHP HttpErrorHandler возвращает
  // {error: {type, description, context: {reason, ...}}} — фактический конверт (форма
  // error.context, подтв. StartSigningTest.php:204 и Error::jsonSerialize).
  // PretensionController.startSigning (raw axios, R-T4-11) кладёт это тело в result[0].
  // error.context — первичный источник; data/error.data оставлены как fallback для
  // совместимости. Выравнивание чинит и латентный locked_by_other (R-SS-7).
  private extractConflictData(payload: any): StartSigningConflictData | null {
    if (payload?.error?.context?.reason) return payload.error.context as StartSigningConflictData;
    if (payload?.context?.reason) return payload.context as StartSigningConflictData;
    if (payload?.data?.reason) return payload.data as StartSigningConflictData;
    if (payload?.error?.data?.reason) return payload.error.data as StartSigningConflictData;
    return null;
  }

  // Текст для CONFLICT_SIGNER_BUSY: номер и время занятой претензии. Если номер не пришёл
  // (holderPretensionNumber === null, отказ по advisory-timeout §6.1) — общий текст без номера.
  get signerBusyText(): string {
    const number = this.conflictData?.holderPretensionNumber;
    if (!number) {
      return this.$t('pretension_signing_modal_signer_busy_elsewhere_generic') as string;
    }
    return this.$t('pretension_signing_modal_signer_busy_elsewhere', {
      number,
      time: this.formatLocalTime(this.conflictData?.lockedAt),
    }) as string;
  }

  get lockedText(): string {
    if (!this.conflictData) return '';
    const params = {
      signerEmail: this.conflictData.previousSignerEmail || '',
      startedAt: this.formatLocalTime(this.conflictData.lockedAt),
      elapsedMinutes: this.elapsedMinutes,
      remainingMinutes: this.remainingMinutes,
    };
    // U-02: при remainingMinutes <= 0 — короткий текст без «~0 минут»
    const key = this.remainingMinutes > 0
      ? 'pretension_signing_modal_locked_text'
      : 'pretension_signing_modal_locked_text_short';
    return this.$t(key, params) as string;
  }

  get elapsedMinutes(): number {
    if (!this.conflictData?.lockedAt) return 0;
    const elapsed = Math.floor((Date.now() - new Date(this.conflictData.lockedAt).getTime()) / 60000);
    return Math.max(0, elapsed);
  }

  get remainingMinutes(): number {
    const ttl = this.conflictData?.lockTtlMinutes ?? LOCK_TTL_MINUTES;
    return Math.max(0, ttl - this.elapsedMinutes);
  }

  // S-02: явный формат HH:MM 24h MSK для кросс-браузерной консистентности
  private formatLocalTime(iso?: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Moscow',
    });
  }

  onClickOk(): void {
    // M-4: явно ставим guard ДО emit, чтобы watcher не отправил повторно
    this.successEmitted = true;
    this.$emit('success');
    this.$emit('input', false);
  }

  emitClose(): void {
    this.$emit('input', false);
  }

  onDialogInput(open: boolean): void {
    if (!open) this.$emit('input', false);
  }
}
